import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CreditCard, Eye, HelpCircle } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import ManagementPage from '../../Common/components/common/ManagementPage';
import { useToast } from '../../Common/context/ToastContext';
import { useTooltip } from '../../Common/context/TooltipContext';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const baseUrl = process.env.REACT_APP_API || '';

/** 전표 공급자 정보 (통합정산 전표용) */
const RECEIPT_SUPPLIER_INFO = {
  supplierName: '-',
  supplierOwnerName: '-',
  supplierBusinessNo: '-',
  supplierContact: '-',
  supplierAddress: '-',
};

/** 전표 카드 가맹점 정보 (통합정산 전표용) */
const RECEIPT_CARD_MERCHANT_INFO = {
  cardMerchantName: '-',
  cardMerchantOwnerName: '-',
  cardMerchantBusinessNo: '-',
};

// UTC → KST 포맷
function formatKST(dateStr, format = 'YYYY.MM.DD. HH:mm') {
  if (!dateStr) return '-';
  return dayjs.utc(dateStr).add(9, 'hour').format(format);
}

/** 최대 길이 초과 시 ... 처리 (줄바꿈 방지용) */
function truncateStr(str, maxLen) {
  if (str == null || str === '') return '-';
  const s = String(str).trim();
  if (s.length === 0) return '-';
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen) + '...';
}

// pg_raw_response에서 카드명 추출 (AppPaymentList와 동일 패턴)
function parseCardNameFromRaw(pgRawResponse) {
  if (!pgRawResponse || typeof pgRawResponse !== 'string') return '-';
  try {
    const parsed = JSON.parse(pgRawResponse);
    const data = parsed?.data;
    return data?.cardNm || data?.cardKind || '-';
  } catch {
    return '-';
  }
}

// 관리자 결제목록 항목 매핑
function mapPaymentTransaction(item) {
  const paymentTransactionId =
    item.payment_transaction_id ??
    item.paymentTransactionId ??
    item.id ??
    item.paymentId ??
    item.payment_id;
  const id = paymentTransactionId;

  const dateStr =
    item.approved_at ??
    item.approvedAt ??
    item.approval_date ??
    item.approvalDate ??
    item.created_at ??
    item.createdAt ??
    item.date;
  const date = dateStr ? dayjs.utc(dateStr).toISOString() : dayjs().toISOString();

  const instmtMon =
    item.instmt_mon ??
    item.instmtMon ??
    item.pg_instmt_mon ??
    item.installmentMonths ??
    item.installment_months ??
    '00';
  const type = instmtMon === '00' ? '일시불' : `${instmtMon}개월`;

  const cardName =
    item.card_nm ??
    item.cardNm ??
    item.cardName ??
    parseCardNameFromRaw(item.pg_raw_response) ??
    '-';

  const status = item.status || '';

  return {
    id,
    paymentTransactionId,
    date,
    pharmacy:
      item.mcht_cust_nm ??
      item.mchtCustNm ??
      item.pharmacy ??
      item.customer_name ??
      item.pharmacy_name ??
      item.customerName ??
      item.pharmacyName ??
      '-',
    amount: Number(
      item.amount ??
      item.trdAmt ??
      item.trd_amt ??
      item.pg_trd_amt ??
      item.request_trd_amt ??
      0
    ),
    approvalNo:
      item.appr_no ??
      item.apprNo ??
      item.approvalNo ??
      item.approval_no ??
      item.approvalNumber ??
      item.original_appr_no ??
      item.originalApprNo ??
      '',
    fee: Number(
      item.cp_fee_amount ??
      item.cpFeeAmount ??
      item.fee_amt ??
      item.feeAmt ??
      item.fee_amount ??
      item.fee ??
      0
    ),
    type,
    status,
    cardName,
    pgProvider:
      item.pg_provider ??
      item.pgProvider ??
      item.provider ??
      '',
    cardNo:
      item.card_no_masked ??
      item.cardNoMasked ??
      '',
    merchant: item.cp_name ?? item.merchant ?? '-',
    driver:
      item.salesperson_name ??
      item.salespersonName ??
      item.salesperson ??
      item.driver ??
      item.driver_name ??
      item.driverName ??
      item.terminal_name ??
      item.terminalName ??
      '',
    cancelStatus:
      String(status).toUpperCase() === 'CANCELED' || item.canceled_at != null,
    canceledAt: item.canceled_at ?? item.canceledAt ?? null,
    // 가맹점 정보 (전표용) - cp_* (채널파트너/가맹점) 우선
    transactionPharmacyName:
      item.transaction_pharmacy_name ?? item.transactionPharmacyName ?? null,
    cpName: item.cp_name ?? item.cpName ?? null,
    cpOwnerName: item.cp_owner_name ?? item.cpOwnerName ?? null,
    cpBusinessNo: item.cp_business_no ?? item.cpBusinessNo ?? null,
    cpPhone: item.cp_phone ?? item.cpPhone ?? null,
    cpAddress: item.cp_address ?? item.cpAddress ?? null,
    pharmacyBusinessNo:
      item.pharmacy_business_no ?? item.pharmacyBusinessNo ?? null,
    pharmacyAddress:
      item.pharmacy_address ?? item.pharmacyAddress ?? null,
    pharmacyContact:
      item.pharmacy_phone ?? item.pharmacy_contact ?? item.pharmacyContact ??
      item.mcht_contact ?? item.mchtContact ?? null,
  };
}

function getStatusLabel(tx) {
  const s = tx.status || '';
  const upper = String(s).toUpperCase();
  if (!upper) return '-';
  // COMPLETED => 승인
  if (upper === 'COMPLETED' || upper.includes('APPROVE') || upper.includes('SUCCESS')) {
    return '승인';
  }
  // CANCEL, CANCELED, CANCELLED 등 => 취소
  if (upper.includes('CANCEL') && upper.includes('PENDING')) {
    return '취소대기';
  }
  if (upper.includes('CANCEL')) {
    return '취소';
  }
  return s;
}

// 날짜 문자열을 YYYY.MM.DD 형식으로 정규화 (26.03.03 → 2026.03.03)
const normalizeDateStr = (s) => {
  if (!s || typeof s !== 'string') return '';
  const t = s.trim().replace(/\.$/, '');
  const parts = t.split('.');
  if (parts.length < 3) return t;
  let [y, m, d] = parts;
  if (y && y.length === 2) y = '20' + y;
  return `${y}.${String(m).padStart(2, '0')}.${String(d).padStart(2, '0')}`;
};

function PaymentList() {
  const { showToast } = useToast();
  const { showAllOpen } = useTooltip();
  const adminSupplierInfo = useSelector((state) => state.adminSupplierInfo?.info);
  const adminPgMerchantRows = useSelector((state) => state.adminPgMerchantInfo?.rows) || [];
  const [hoveredTooltipId, setHoveredTooltipId] = useState(null);
  const location = useLocation();
  useEffect(() => {
    if (!showAllOpen) setHoveredTooltipId(null);
  }, [showAllOpen]);

  // 기본 기간: 당월 1일 ~ 오늘 (또는 location.state.searchDate가 있으면 해당 날짜)
  const getDefaultMonthRange = () => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;

    // 정산관리에서 전달된 날짜가 있으면 해당 날짜로 설정 (단일: "2026.02.11", 범위: "2026.02.11~2026.02.12")
    if (location.state?.searchDate) {
      const sd = String(location.state.searchDate).trim();
      if (sd.includes('~')) {
        const [s, e] = sd.split('~').map((x) => normalizeDateStr(x)).filter(Boolean);
        return {
          start: s || formatDate(today),
          end: e || formatDate(today),
        };
      }
      const norm = normalizeDateStr(sd);
      return {
        start: norm || formatDate(today),
        end: norm || formatDate(today),
      };
    }

    return {
      start: formatDate(monthStart),
      end: formatDate(today),
    };
  };
  const defaultRange = getDefaultMonthRange();
  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);

  // 정산관리에서 searchDate로 이동 시 날짜 동기화 (location.state는 네비게이션 직후에 반영)
  useEffect(() => {
    const sd = location.state?.searchDate;
    if (!sd) return;
    const str = String(sd).trim();
    const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    if (str.includes('~')) {
      const parts = str.split('~').map((x) => normalizeDateStr(x)).filter(Boolean);
      if (parts.length >= 2) {
        setStartDate(parts[0]);
        setEndDate(parts[1]);
      } else if (parts.length === 1) {
        setStartDate(parts[0]);
        setEndDate(parts[0]);
      }
    } else {
      const norm = normalizeDateStr(str);
      if (norm) {
        setStartDate(norm);
        setEndDate(norm);
      }
    }
  }, [location.state?.searchDate]);
  const [searchInput, setSearchInput] = useState(''); // 검색창 입력값
  const [searchQuery, setSearchQuery] = useState(''); // API q 파라미터 (검색 버튼/Enter 시 적용)
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ totalAmount: 0, totalCount: 0 });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const infiniteScrollRef = useRef(null);
  const pageRef = useRef(page);
  const hasNextRef = useRef(hasNext);
  pageRef.current = page;
  hasNextRef.current = hasNext;
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);
  const [sendMethod, setSendMethod] = useState('email'); // 'email' | 'sms' | 'kakao'
  const [receiptPhone, setReceiptPhone] = useState('');
  const [receiptPreviewData, setReceiptPreviewData] = useState(null);
  const [receiptViewMode, setReceiptViewMode] = useState('approval');
  const [revealModalOpen, setRevealModalOpen] = useState(false);
  const [revealPassword, setRevealPassword] = useState('');
  const [revealReason, setRevealReason] = useState('');
  const [revealLoading, setRevealLoading] = useState(false);
  const receiptSupplierInfo = {
    supplierName: adminSupplierInfo?.name || RECEIPT_SUPPLIER_INFO.supplierName,
    supplierOwnerName: adminSupplierInfo?.owner_name || RECEIPT_SUPPLIER_INFO.supplierOwnerName,
    supplierBusinessNo: adminSupplierInfo?.business_no || RECEIPT_SUPPLIER_INFO.supplierBusinessNo,
    supplierContact: adminSupplierInfo?.contact || RECEIPT_SUPPLIER_INFO.supplierContact,
    supplierAddress: adminSupplierInfo?.address || RECEIPT_SUPPLIER_INFO.supplierAddress,
  };

  const getReceiptCardMerchantInfo = useCallback((pgProvider) => {
    const provider = String(pgProvider || '').toUpperCase();
    const matched = adminPgMerchantRows.find(
      (row) => String(row?.pg_provider || '').toUpperCase() === provider,
    );
    if (!matched) return RECEIPT_CARD_MERCHANT_INFO;
    return {
      cardMerchantName: matched.name || RECEIPT_CARD_MERCHANT_INFO.cardMerchantName,
      cardMerchantOwnerName: matched.owner_name || RECEIPT_CARD_MERCHANT_INFO.cardMerchantOwnerName,
      cardMerchantBusinessNo: matched.business_no || RECEIPT_CARD_MERCHANT_INFO.cardMerchantBusinessNo,
    };
  }, [adminPgMerchantRows]);

  // 영수증 미리보기 모달 열기 (IntegratedSettlement와 동일)
  const openReceiptPreview = useCallback((item) => {
    if (!item) return;
    const amount = Number(item.amount || 0);
    const cardNumberMasked = '****-****-****-****';
    const taxableAmount = Math.round(amount / 1.1);
    const vatAmount = amount - taxableAmount;
    const taxFreeAmount = 0;
    const serviceAmount = 0;
    const approvalDateTime = formatKST(item.date, 'YYYY.MM.DD. HH:mm:ss');
    const cancelDateTime = item.canceledAt ? formatKST(item.canceledAt, 'YYYY.MM.DD. HH:mm:ss') : null;
    const isCanceled = !!item.cancelStatus;
    const receiptCardMerchantInfo = getReceiptCardMerchantInfo(item.pgProvider);
    setReceiptPreviewData({
      paymentTransactionId: item.paymentTransactionId || '',
      cardBrand: item.cardName || '-',
      cardNumberMasked,
      installmentMonth: item.type || '일시불',
      approvalNumber: item.approvalNo || '-',
      approvalDateTime,
      cancelDateTime,
      isCanceled,
      pharmacyName: item.pharmacy || '-',
      taxableAmount: taxableAmount.toLocaleString('ko-KR') + '원',
      vatAmount: vatAmount.toLocaleString('ko-KR') + '원',
      taxFreeAmount: taxFreeAmount.toLocaleString('ko-KR') + '원',
      serviceAmount: serviceAmount.toLocaleString('ko-KR') + '원',
      totalAmount: amount.toLocaleString('ko-KR') + '원',
      ...receiptSupplierInfo,
      merchantName:
        item.cpName ||
        '-',
      merchantOwnerName: item.cpOwnerName || item.driver || '-',
      merchantBusinessNo: item.cpBusinessNo || item.pharmacyBusinessNo || '-',
      merchantContact: item.cpPhone || item.pharmacyContact || '-',
      merchantAddress: item.cpAddress || item.pharmacyAddress || '-',
      ...receiptCardMerchantInfo,
    });
    setReceiptViewMode('approval');
    setReceiptModalOpen(true);
    setReceiptEmail('');
  }, [getReceiptCardMerchantInfo, receiptSupplierInfo]);

  const handleRevealCardNumber = useCallback(async () => {
    if (!receiptPreviewData?.paymentTransactionId) {
      showToast('전표 ID가 없습니다.');
      return;
    }
    const password = (revealPassword || '').trim();
    if (!password) {
      showToast('비밀번호를 입력해 주세요.');
      return;
    }
    const reason = (revealReason || '').trim();
    if (!reason) {
      showToast('조회 사유를 입력해 주세요.');
      return;
    }
    setRevealLoading(true);
    try {
      const { data } = await axios.post(
        baseUrl + '/api/admin/payment-transactions/reveal-card',
        {
          paymentTransactionId: receiptPreviewData.paymentTransactionId,
          password,
          reason,
        },
        { withCredentials: true }
      );
      if (data?.code !== 1000) throw new Error(data?.msg || '카드번호 조회에 실패했습니다.');
      const masked = data?.data?.card_no_masked;
      if (!masked) throw new Error('카드번호를 가져오지 못했습니다.');
      setReceiptPreviewData((prev) => (prev ? { ...prev, cardNumberMasked: masked } : prev));
      setRevealModalOpen(false);
      setRevealPassword('');
      setRevealReason('');
      showToast('카드번호가 조회되었습니다.');
    } catch (e) {
      showToast(e.message || '카드번호 조회에 실패했습니다.', 'error');
    } finally {
      setRevealLoading(false);
    }
  }, [receiptPreviewData, revealPassword, revealReason, showToast]);

  const handleConfirmSendReceipt = useCallback(async () => {
    if (!receiptPreviewData?.paymentTransactionId) {
      showToast('전표 ID가 없습니다.', 'error');
      return;
    }

    const closeReceipt = () => {
      setReceiptModalOpen(false);
      setReceiptEmail('');
      setReceiptPhone('');
      setSendMethod('email');
      setReceiptPreviewData(null);
      setReceiptViewMode('approval');
    };

    if (sendMethod === 'email') {
      const email = (receiptEmail || '').trim();
      if (!email) {
        showToast('전송할 이메일을 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/admin/payment-transactions/receipt-email',
          { paymentTransactionId: receiptPreviewData.paymentTransactionId, email },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '이메일로 전표가 전송되었습니다.');
          closeReceipt();
        } else {
          showToast(data?.msg || '전표 이메일 전송에 실패했습니다.', 'error');
        }
      } catch (err) {
        showToast(err?.response?.data?.msg || '전표 이메일 전송 중 오류가 발생했습니다.', 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    } else if (sendMethod === 'sms') {
      const phone = (receiptPhone || '').replace(/[^0-9]/g, '');
      if (!phone) {
        showToast('휴대폰 번호를 입력해주세요.', 'error');
        return;
      }
      try {
        setIsSendingReceipt(true);
        const { data } = await axios.post(
          baseUrl + '/api/admin/payment-transactions/receipt-sms',
          { paymentTransactionId: receiptPreviewData.paymentTransactionId, phone },
          { withCredentials: true }
        );
        if (data?.code === 1000) {
          showToast(data?.msg || '전표 문자가 전송되었습니다.');
          closeReceipt();
        } else {
          showToast(data?.data?.message || data?.msg || '전표 문자 전송에 실패했습니다.', 'error');
        }
      } catch (err) {
        showToast(err?.response?.data?.data?.message || err?.response?.data?.msg || '전표 문자 전송 중 오류가 발생했습니다.', 'error');
      } finally {
        setIsSendingReceipt(false);
      }
    }
  }, [sendMethod, receiptEmail, receiptPhone, receiptPreviewData, showToast]);

  // CSV 유틸 (엑셀 다운로드용) - 표 순서와 동일하게
  const buildCsv = (data) => {
    const columns = [
      { key: 'date', label: '결제일' },
      { key: 'approvalNo', label: '승인번호' },
      { key: 'merchant', label: '가맹점' },
      { key: 'pharmacy', label: '약국' },
      { key: 'driver', label: '영업자' },
      { key: 'cardName', label: '카드사' },
      { key: 'cardNo', label: '카드번호' },
      { key: 'type', label: '할부' },
      { key: 'amount', label: '금액' },
      { key: 'fee', label: '수수료' },
      { key: 'status', label: '상태' },
    ];

    const escapeCell = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).replace(/"/g, '""');
    };

    const header = columns.map((c) => `"${c.label}"`).join(',');
    const lines = data.map((row) => {
      return columns
        .map((c) => {
          let v = row[c.key];
          if (c.key === 'date') {
            v = formatKST(row.date, 'YYYY.MM.DD. HH:mm');
          } else if (c.key === 'amount') {
            v = Number(row.amount || 0).toLocaleString('ko-KR');
          } else if (c.key === 'fee') {
            v = Number(row.fee ?? 0).toLocaleString('ko-KR');
          } else if (c.key === 'status') {
            v = getStatusLabel(row);
          }
          return `"${escapeCell(v ?? '')}"`;
        })
        .join(',');
    });

    // UTF-8 BOM 추가해서 윈도우 엑셀에서 한글 안 깨지도록
    return '\uFEFF' + header + '\r\n' + lines.join('\r\n');
  };

  const setQuickPeriod = (type) => {
    const today = new Date();
    const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    const parseDate = (s) => {
      const [y, m, d] = s.split('.').map(Number);
      return new Date(y, m - 1, d);
    };
    const currentStart = parseDate(startDate);
    const currentEnd = parseDate(endDate);
    let newStart, newEnd;
    switch (type) {
      case '당일': newStart = newEnd = today; break;
      case '전일':
        // 마지막날짜( endDate ) 기준 전일: 1/1~1/31 → 1/30~1/30, 다시 전일 → 1/29~1/29
        newEnd = new Date(currentEnd);
        newEnd.setDate(currentEnd.getDate() - 1);
        newStart = new Date(newEnd);
        break;
      case '당월': newStart = new Date(today.getFullYear(), today.getMonth(), 1); newEnd = today; break;
      case '전월':
        newStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 1);
        newEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0);
        break;
      default: return;
    }
    setStartDate(formatDate(newStart));
    setEndDate(formatDate(newEnd));
  };

  const totalCount = summary.totalCount ?? 0;
  const totalAmount = summary.totalAmount ?? 0;

  // 기간을 한국시간(KST) 기준으로 해석 후 UTC ISO 문자열로 API 전달
  const safeStart = (startDate && !String(startDate).includes('~')) ? startDate.replace(/\./g, '-') : null;
  const safeEnd = (endDate && !String(endDate).includes('~')) ? endDate.replace(/\./g, '-') : null;
  const dateFrom = safeStart
    ? dayjs.utc(safeStart + 'T00:00:00').subtract(9, 'hour').toISOString()
    : dayjs().subtract(9, 'hour').toISOString();
  const dateTo = safeEnd
    ? dayjs.utc(safeEnd + 'T23:59:59.999').subtract(9, 'hour').toISOString()
    : dayjs().subtract(9, 'hour').toISOString();

  // 결제목록 조회 (초기 또는 검색/기간 변경 시 page=1)
  const fetchList = useCallback(
    async (pageNum = 1, append = false) => {
      if (append && loadingMoreRef.current) return;
      const baseUrl = process.env.REACT_APP_API || '';
      const params = { dateFrom, dateTo, page: pageNum, limit: 100 };
      if (searchQuery.trim()) params.q = searchQuery.trim();

      if (!append) setLoading(true);
      else {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      }

      try {
        const listRes = await axios.get(baseUrl + '/api/admin/payment-transactions', {
          params,
          withCredentials: true,
        });
        const listData = listRes.data;

        if (listData?.code !== 1000) {
          if (!append) {
            setRows([]);
            setSummary({ totalAmount: 0, totalCount: 0 });
            showToast(listData?.msg || '결제내역을 불러오는 중 오류가 발생했습니다.', 'error');
          }
          return;
        }

        const d = listData.data || {};
        let listRows = Array.isArray(d.rows) ? d.rows : Array.isArray(d) ? d : [];
        const mappedRows = listRows.map(mapPaymentTransaction);
        
        const totalAmount = Number(d.summary?.completedAmount ?? 0);
        const totalCount = Number(d.total ?? 0);
        const limitNum = Number(d.limit ?? params.limit ?? 100);
        const totalNum = Number(d.total ?? 0);
        const nextHasNext = d.hasNext !== undefined
          ? !!d.hasNext
          : pageNum * limitNum < totalNum;

        if (append) {
          setRows((prev) => [...prev, ...mappedRows]);
        } else {
          setRows(mappedRows);
        }
        setSummary({ totalAmount, totalCount });
        setHasNext(nextHasNext);
        setPage(pageNum);
      } catch (e) {
        if (!append) {
          setRows([]);
          setSummary({ totalAmount: 0, totalCount: 0 });
          showToast(e?.response?.data?.msg || '결제내역을 불러오는 중 오류가 발생했습니다.', 'error');
        } else {
          showToast('추가 데이터를 불러오는 데 실패했습니다.', 'error');
        }
      } finally {
        setLoading(false);
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    },
    [dateFrom, dateTo, searchQuery, showToast]
  );

  // 초기 조회 및 검색/기간 변경 시
  useEffect(() => {
    fetchList(1, false);
  }, [dateFrom, dateTo, searchQuery, fetchList]);

  // 무한 스크롤: IntersectionObserver로 하단 감지 (스크롤바 없을 때도 동작)
  // rows 로드 후 DOM 확정 시점에 observer 등록 (sentinel이 자식 컴포넌트에 있어 타이밍 이슈 방지)
  useEffect(() => {
    let observer = null;
    const id = requestAnimationFrame(() => {
      const el = infiniteScrollRef.current;
      if (!el) {
        return;
      }
      observer = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry?.isIntersecting || loadingMoreRef.current || !hasNextRef.current) return;
          fetchList(pageRef.current + 1, true);
        },
        { rootMargin: '100px', threshold: 0 }
      );
      observer.observe(el);
    });
    return () => {
      cancelAnimationFrame(id);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [fetchList, rows.length]);

  return (
    <>
      <Tooltip id="payment-total-tooltip" place="right" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'payment-total'} setIsOpen={() => {}} />
      <Tooltip id="payment-excel-download-tooltip" place="left" positionStrategy="absolute" className="max-w-[300px] text-[11px] z-[9999] whitespace-pre-line" isOpen={showAllOpen || hoveredTooltipId === 'payment-excel-download'} setIsOpen={() => {}} />
      <ManagementPage
        title="결제내역"
        emptyStateIcon={<CreditCard size={32} className="opacity-20" />}
        hideSearch={true}
        hideDetail={true}
        filters={
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-[40px] flex items-center gap-2 bg-white px-3 rounded-lg border border-[#e5e8eb] focus-within:border-[#0064ff] transition-all shrink-0 w-[250px]">
              <input
                type="text"
                placeholder="약국명·가맹점 검색"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setSearchQuery(searchInput.trim());
                }}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] py-0 h-full flex-1 min-w-0"
              />
              <button
                type="button"
                onClick={() => setSearchQuery(searchInput.trim())}
                className="px-2 py-1 rounded bg-[#0064ff] text-white text-[12px] font-bold hover:opacity-90 shrink-0"
              >
                검색
              </button>
            </div>
            <div className="h-4 w-[1px] bg-[#e5e8eb]" />
            <div className="h-[40px] flex items-center gap-1.5 bg-white px-3 rounded-lg border border-[#e5e8eb] focus-within:border-[#0064ff] transition-all">
              <input
                type="date"
                value={startDate.replace(/\./g, '-')}
                onChange={(e) => setStartDate(e.target.value.replace(/-/g, '.'))}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] tabular-nums py-0 h-full"
              />
              <span className="text-[#8b95a1] text-[12px]">~</span>
              <input
                type="date"
                value={endDate.replace(/\./g, '-')}
                onChange={(e) => setEndDate(e.target.value.replace(/-/g, '.'))}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] tabular-nums py-0 h-full"
              />
            </div>
            <div className="flex gap-1">
              {['당일', '전일', '당월', '전월'].map((type) => (
                <button
                  key={type}
                  onClick={() => setQuickPeriod(type)}
                  className="px-2.5 py-1.5 bg-white border border-[#e5e8eb] rounded-lg text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] leading-none h-[40px] flex items-center"
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        }
        leftActions={
          <div className="text-[16px] font-bold text-[#191f28] flex items-center gap-1.5">
            결제 금액: <span className="text-[#0064ff]">{totalAmount.toLocaleString()}원</span> ({totalCount}건)
            <span
              data-tooltip-id="payment-total-tooltip"
              data-tooltip-html="조회 기간 내 결제 금액의 합계입니다.<br/>당일 취소 외 취소 건은 검색 기간에 상계처리됩니다."
              onMouseEnter={() => setHoveredTooltipId('payment-total')}
              onMouseLeave={() => setHoveredTooltipId(null)}
              className="inline-flex cursor-help text-slate-400 hover:text-slate-600 transition-colors"
            >
              <HelpCircle size={14} strokeWidth={2} />
            </span>
          </div>
        }
        onExcelDownload={() => {
          if (!rows || rows.length === 0) {
            showToast('다운로드할 결제내역이 없습니다.');
            return;
          }

          const csv = buildCsv(rows);
          const blob = new Blob([csv], {
            type: 'text/csv;charset=utf-8;',
          });
          const url = URL.createObjectURL(blob);

          const safeStart = startDate.replace(/\./g, '');
          const safeEnd = endDate.replace(/\./g, '');
          const filename = `결제내역_${safeStart}_${safeEnd}.csv`;

          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
          showToast('엑셀 다운로드가 시작되었습니다.');
        }}
        excelButtonClassName="inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors"
        excelButtonTooltipProps={{
          'data-tooltip-id': 'payment-excel-download-tooltip',
          'data-tooltip-html': '건별 결제 내역을 다운로드합니다.',
          onMouseEnter: () => setHoveredTooltipId('payment-excel-download'),
          onMouseLeave: () => setHoveredTooltipId(null),
        }}
        tableHeaders={
          <>
            <th className="px-3 py-1 w-[125px] text-[12px]">결제일</th>
            <th className="px-3 py-1 w-[110px] text-[12px]">승인번호</th>
            <th className="px-3 py-1 min-w-[120px] text-[12px]">가맹점</th>
            <th className="px-3 py-1 text-[12px]">약국</th>
            <th className="px-3 py-1 min-w-[100px] text-[12px]">영업자</th>
            <th className="px-3 py-1 w-[140px] text-[12px]">카드사</th>
            <th className="px-3 py-1 w-[160px] text-[12px]">카드번호</th>
            <th className="px-3 py-1 w-[140px] text-[12px]">할부</th>
            <th className="px-3 py-1 text-right w-[200px] text-[12px]">금액</th>
            <th className="px-3 py-1 text-right w-[200px] text-[12px]">수수료</th>
            <th className="px-3 py-1 text-[12px]">상태</th>
            <th className="px-3 py-1 text-center w-[80px] text-[12px]">전표</th>
          </>
        }
        data={rows}
        getRowClassName={(item) => {
          const isC = String(item.status || '').toUpperCase().includes('CANCEL');
          return isC ? 'canceled-row' : '';
        }}
        infiniteScrollRef={infiniteScrollRef}
        renderRow={(item) => {
          const isCanceled = String(item.status || '')
            .toUpperCase()
            .includes('CANCEL');
          const canceledClass = isCanceled ? ' bg-[#fef2f2]' : '';
          const amount = Number(item.amount || 0);
          const fee = Number(item.fee ?? 0);
          return (
            <>
              <td className={`px-3 py-1 w-[125px] text-[#4e5968] whitespace-nowrap text-[12px] tabular-nums ${canceledClass}`}>
                {formatKST(item.date, 'YYYY.MM.DD. HH:mm')}
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap text-[12px] tabular-nums ${canceledClass}`}>
                {item.approvalNo || '-'}
              </td>
              <td className={`px-3 py-1 min-w-[120px] text-[#4e5968] whitespace-nowrap text-[12px]${canceledClass}`}>
                {item.merchant || '-'}
              </td>
              <td className={`px-3 py-1 min-w-[140px] text-[#191f28] whitespace-nowrap text-[12px]${canceledClass}`}>
                {item.pharmacy || '-'}
              </td>
              <td className={`px-3 py-1 min-w-[100px] text-[#4e5968] whitespace-nowrap text-[12px]${canceledClass}`}>
                {item.driver || '-'}
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap text-[12px]${canceledClass}`} title={item.cardName || ''}>
                {truncateStr(item.cardName, 8)}
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap font-mono text-[12px] tabular-nums${canceledClass}`} title={item.cardNo || ''}>
                {item.cardNo || '-'}
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap text-[12px]${canceledClass}`}>
                {item.type || '-'}
              </td>
              <td className={`px-3 py-1 text-right text-[#191f28] tabular-nums whitespace-nowrap text-[12px]${canceledClass}`}>
                {amount.toLocaleString('ko-KR')}원
              </td>
              <td className={`px-3 py-1 text-right text-[#4e5968] tabular-nums whitespace-nowrap text-[12px]${canceledClass}`}>
                {fee.toLocaleString('ko-KR')}원
              </td>
              <td className={`px-3 py-1 text-[#4e5968] whitespace-nowrap text-[12px]${canceledClass}`}>
                {getStatusLabel(item)}
              </td>
              <td className={`px-3 py-1 text-center whitespace-nowrap text-[12px]${canceledClass} ${isCanceled ? 'relative z-[2]' : ''}`}>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    openReceiptPreview(item);
                  }}
                  className="px-2 py-0.5 rounded text-[12px] font-semibold text-[#0064ff] border border-[#0064ff] bg-white hover:bg-[#e7f5ff] shrink-0"
                >
                  전표
                </button>
              </td>
            </>
          );
        }}
      />
      {/* 신용카드 매출전표 모달 (receipt1.html UI 기준) */}
      {receiptModalOpen && receiptPreviewData && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000] overflow-y-auto py-8" onClick={() => { if (isSendingReceipt) return; setReceiptModalOpen(false); setReceiptEmail(''); setReceiptPhone(''); setSendMethod('email'); setReceiptPreviewData(null); setReceiptViewMode('approval'); }}>
          <div className="bg-white rounded border border-[#e0e0e0] w-[360px] shadow-xl my-auto flex flex-col max-h-[80vh]" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white z-10 px-5 pt-4 pb-4 border-b border-[#333333] flex items-center justify-between gap-4">
              <div className="text-[20px] font-bold text-[#111111] leading-tight">신용카드 매출전표</div>
              {receiptPreviewData.isCanceled && (
                <div className="flex rounded-lg overflow-hidden border border-[#e5e7eb] shrink-0">
                  <button
                    type="button"
                    onClick={() => setReceiptViewMode('approval')}
                    className={`px-3 py-1.5 text-[13px] font-semibold ${receiptViewMode === 'approval'
                      ? 'bg-[#0064ff] text-white border-[#0064ff]'
                      : 'bg-white text-[#0064ff] border border-transparent hover:bg-[#e7f5ff]'
                      }`}
                  >
                    승인
                  </button>
                  <button
                    type="button"
                    onClick={() => setReceiptViewMode('cancel')}
                    className={`px-3 py-1.5 text-[13px] font-semibold ${receiptViewMode === 'cancel'
                      ? 'bg-[#b91c1c] text-white border-[#b91c1c]'
                      : 'bg-white text-[#b91c1c] border border-transparent hover:bg-[#fef2f2]'
                      }`}
                  >
                    취소
                  </button>
                </div>
              )}
            </div>
            <div className="overflow-y-auto flex-1 px-5 py-5 min-w-0">
              <div className="mb-0">
                <div className="text-[16px] font-bold text-[#111111] pb-3">거래정보</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">카드종류</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardBrand}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">카드번호</span>
                    <div className="flex items-center gap-2 ml-auto min-w-0">
                      <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardNumberMasked}</span>
                      <button type="button" onClick={() => setRevealModalOpen(true)} className="inline-flex items-center justify-center w-7 h-7 rounded-full border border-[#d1d5db] text-[#4b5563] hover:bg-[#f3f4f6] shrink-0">
                        <Eye size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">할부</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.installmentMonth}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">거래유형</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptViewMode === 'approval' ? '승인' : '취소'}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">승인번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.approvalNumber}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">{receiptViewMode === 'approval' ? '거래일시' : '취소일시'}</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 leading-snug">{receiptViewMode === 'approval' ? receiptPreviewData.approvalDateTime : (receiptPreviewData.cancelDateTime ?? '-')}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">거래처명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.pharmacyName}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">공급대가</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">과세금액</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.taxableAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">부가세</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.vatAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">면세금액</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.taxFreeAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">봉사료</span>
                    <span className="text-[#111111] font-bold text-right min-w-0 tabular-nums">{receiptPreviewData.serviceAmount}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#111111] font-bold text-[15px] shrink-0 w-[120px]">총 결제금액</span>
                    <span className="text-[#f04438] font-bold text-[16px] text-right min-w-0 tabular-nums">{receiptPreviewData.totalAmount}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">가맹점 정보</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">상호명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.merchantName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.merchantOwnerName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.merchantBusinessNo}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.merchantContact}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">사업장소재지</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">{receiptPreviewData.merchantAddress}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div>
                <div className="text-[16px] font-bold text-[#111111] pb-3">공급자 정보</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">상호명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.supplierName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.supplierOwnerName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.supplierBusinessNo}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">이용문의</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.supplierContact}</span>
                  </div>
                  <div className="flex justify-between items-start py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px] pt-0.5">사업장소재지</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 break-words leading-snug">{receiptPreviewData.supplierAddress}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div className="mb-5">
                <div className="text-[16px] font-bold text-[#111111] pb-3">카드 가맹점 정보</div>
                <div className="text-[14px]">
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">가맹점명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardMerchantName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">대표자명</span>
                    <span className="text-[#111111] font-medium text-right min-w-0">{receiptPreviewData.cardMerchantOwnerName}</span>
                  </div>
                  <div className="flex justify-between items-center py-1.5">
                    <span className="text-[#767676] shrink-0 w-[120px]">사업자등록번호</span>
                    <span className="text-[#111111] font-medium text-right min-w-0 tabular-nums">{receiptPreviewData.cardMerchantBusinessNo}</span>
                  </div>
                </div>
              </div>
              <div className="border-b border-[#e0e0e0] my-3.5" />
              <div className="text-[11px] leading-relaxed text-[#999999]">
                본 전표는 이메일 전송용으로 구성된 내역이며, 결제/정산 시스템의 원장과 차이가 있을 수 있습니다.
              </div>
            </div>
            <div className="border-t border-[#eeeeee] p-5 bg-[#fafafa] shrink-0">
              {/* 전송 방법 선택 */}
              <div className="flex rounded-lg overflow-hidden border border-[#e5e7eb] mb-3">
                <button type="button" onClick={() => setSendMethod('email')} className={`flex-1 py-2 text-[13px] font-semibold ${sendMethod === 'email' ? 'bg-[#0064ff] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>이메일</button>
                <button type="button" onClick={() => setSendMethod('sms')} className={`flex-1 py-2 text-[13px] font-semibold border-x border-[#e5e7eb] ${sendMethod === 'sms' ? 'bg-[#00b493] text-white' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>문자</button>
                <button type="button" onClick={() => setSendMethod('kakao')} className={`flex-1 py-2 text-[13px] font-semibold ${sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e]' : 'bg-white text-[#4e5968] hover:bg-[#f2f4f6]'}`}>카카오톡(준비중)</button>
              </div>
              {sendMethod === 'email' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">이메일 주소</label>
                  <input
                    type="email"
                    value={receiptEmail}
                    onChange={(e) => setReceiptEmail(e.target.value)}
                    autoComplete="off"
                    placeholder="example@email.com"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                    className="w-full mb-3 px-3 py-1.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[#0064ff]"
                  />
                </>
              )}
              {sendMethod === 'sms' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                  <input
                    type="tel"
                    value={receiptPhone}
                    onChange={(e) => setReceiptPhone(e.target.value)}
                    placeholder="01012345678"
                    onKeyDown={(e) => { if (e.key === 'Enter' && !isSendingReceipt) handleConfirmSendReceipt(); }}
                    className="w-full mb-3 px-3 py-1.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none focus:border-[#0064ff]"
                  />
                </>
              )}
              {sendMethod === 'kakao' && (
                <>
                  <label className="block text-[13px] font-semibold text-[#4e5968] mb-1.5">휴대폰 번호</label>
                  <input type="tel" disabled placeholder="01012345678" className="w-full mb-3 px-3 py-1.5 rounded-lg border border-[#d1d5db] text-[14px] outline-none bg-[#f2f4f6] text-[#adb5bd] cursor-not-allowed" />
                </>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (isSendingReceipt) return;
                    setReceiptModalOpen(false);
                    setReceiptEmail('');
                    setReceiptPhone('');
                    setSendMethod('email');
                    setReceiptPreviewData(null);
                    setReceiptViewMode('approval');
                  }}
                  className="flex-1 px-3 py-1.5 rounded-lg text-[14px] font-semibold bg-[#e5e7eb] text-[#111827] hover:bg-[#d1d5db]"
                >
                  닫기
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSendReceipt}
                  disabled={isSendingReceipt || sendMethod === 'kakao'}
                  className={`flex-1 px-3 py-1.5 rounded-lg text-[14px] font-semibold ${sendMethod === 'kakao' ? 'bg-[#fee500] text-[#3c1e1e] cursor-not-allowed' : isSendingReceipt ? 'bg-[#93c5fd] text-white cursor-not-allowed' : sendMethod === 'sms' ? 'bg-[#00b493] text-white hover:bg-[#009a7f]' : 'bg-[#0064ff] text-white hover:bg-[#0052cc]'}`}
                >
                  {isSendingReceipt ? '전송중...' : sendMethod === 'kakao' ? '준비중' : sendMethod === 'sms' ? '문자 전송' : '이메일 전송'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 카드번호 조회 모달 */}
      {revealModalOpen && receiptPreviewData && (
        <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-[#e5e7eb] flex items-center justify-between">
              <h2 className="text-[16px] font-bold text-[#111827]">카드번호 조회</h2>
              <button
                type="button"
                onClick={() => { if (revealLoading) return; setRevealModalOpen(false); }}
                className="text-[#9ca3af] hover:text-[#111827] text-[18px] leading-none px-1"
              >
                ×
              </button>
            </div>
            <div className="px-5 py-4 space-y-3 text-[13px] text-[#374151]">
              <p className="text-[13px] text-[#4b5563]">
                카드번호는 개인정보 보호를 위해 기본적으로 완전 마스킹 처리됩니다.
                카드번호의 앞 6자리/뒤 4자리를 확인하려면, 아래 정보를 입력해 주세요.
              </p>
              <div className="space-y-2">
                <label className="block text-[12px] font-semibold text-[#6b7280]">비밀번호</label>
                <input
                  type="password"
                  value={revealPassword}
                  onChange={(e) => setRevealPassword(e.target.value)}
                  autoComplete="off"
                  className="w-full px-3 py-1 border border-[#d1d5db] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0064ff]"
                  placeholder="로그인 비밀번호를 입력하세요"
                />
              </div>
              <div className="space-y-2">
                <label className="block text-[12px] font-semibold text-[#6b7280]">조회 사유</label>
                <input
                  type="text"
                  value={revealReason}
                  onChange={(e) => setRevealReason(e.target.value)}
                  autoComplete="off"
                  className="w-full px-3 py-1 border border-[#d1d5db] rounded-lg text-[13px] focus:outline-none focus:ring-1 focus:ring-[#0064ff]"
                  placeholder="예: 카드사 문의, 고객 요청 등"
                />
              </div>
              <p className="text-[11px] text-[#9ca3af]">
                입력하신 정보는 카드번호 조회 내역과 함께 감사 로그로 기록될 수 있습니다.
              </p>
            </div>
            <div className="px-5 py-3 border-t border-[#e5e7eb] flex justify-end gap-2 bg-[#f9fafb]">
              <button
                type="button"
                onClick={() => { if (revealLoading) return; setRevealModalOpen(false); }}
                className="px-4 py-1 rounded-xl text-[13px] font-bold bg-white text-[#4b5563] border border-[#d1d5db]"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleRevealCardNumber}
                disabled={revealLoading}
                className="px-4 py-1 rounded-xl text-[13px] font-bold bg-[#111827] text-white disabled:opacity-60"
              >
                {revealLoading ? '조회 중...' : '조회'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PaymentList;
